import type { VisualEdge, VisualNode } from "@/types/knowledgeGraph";

/**
 * Build an adjacency list from nodes and edges
 */
function buildAdjacencyList(nodes: VisualNode[], edges: VisualEdge[]): Map<string, Set<string>> {
	const adjacency = new Map<string, Set<string>>();

	// Initialize all nodes
	nodes.forEach((node) => {
		adjacency.set(node.id, new Set());
	});

	// Add edges (undirected graph)
	edges.forEach((edge) => {
		const source: string = typeof edge.source === "string" ? edge.source : (edge.source as VisualNode).id;
		const target: string = typeof edge.target === "string" ? edge.target : (edge.target as VisualNode).id;

		adjacency.get(source)?.add(target);
		adjacency.get(target)?.add(source);
	});

	return adjacency;
}

/**
 * Detect communities using BFS (connected components)
 * This is a fallback when Louvain algorithm is not available
 */
export function detectCommunitiesBFS(nodes: VisualNode[], edges: VisualEdge[]): Map<string, number> {
	const communities = new Map<string, number>();
	const adjacency = buildAdjacencyList(nodes, edges);
	const visited = new Set<string>();
	let communityId = 0;

	const bfs = (startId: string, cid: number) => {
		const queue = [startId];
		visited.add(startId);
		communities.set(startId, cid);

		while (queue.length > 0) {
			const currentId = queue.shift();
			if (!currentId) break;
			const neighbors = adjacency.get(currentId) || new Set();

			for (const neighborId of neighbors) {
				if (!visited.has(neighborId)) {
					visited.add(neighborId);
					communities.set(neighborId, cid);
					queue.push(neighborId);
				}
			}
		}
	};

	// Find all connected components
	nodes.forEach((node) => {
		if (!visited.has(node.id)) {
			bfs(node.id, communityId);
			communityId++;
		}
	});

	return communities;
}

/**
 * Calculate degree centrality for all nodes
 */
export function calculateDegreeCentrality(nodes: VisualNode[], edges: VisualEdge[]): Map<string, number> {
	const degree = new Map<string, number>();

	// Initialize all nodes with degree 0
	nodes.forEach((node) => {
		degree.set(node.id, 0);
	});

	// Count edges for each node (undirected graph)
	edges.forEach((edge) => {
		const source: string = typeof edge.source === "string" ? edge.source : (edge.source as VisualNode).id;
		const target: string = typeof edge.target === "string" ? edge.target : (edge.target as VisualNode).id;

		degree.set(source, (degree.get(source) || 0) + 1);
		degree.set(target, (degree.get(target) || 0) + 1);
	});

	return degree;
}

/**
 * Calculate betweenness centrality using Brandes' algorithm
 * Simplified version for undirected graphs
 */
export function calculateBetweennessCentrality(nodes: VisualNode[], edges: VisualEdge[]): Map<string, number> {
	const betweenness = new Map<string, number>();
	const adjacency = buildAdjacencyList(nodes, edges);

	// Initialize all nodes with 0 betweenness
	nodes.forEach((node) => {
		betweenness.set(node.id, 0);
	});

	// For each node, run BFS to calculate shortest paths
	nodes.forEach((startNode) => {
		const distances = new Map<string, number>();
		const paths = new Map<string, number>();
		const predecessors = new Map<string, string[]>();
		const queue: string[] = [];

		// Initialize
		nodes.forEach((node) => {
			distances.set(node.id, -1);
			paths.set(node.id, 0);
			predecessors.set(node.id, []);
		});

		distances.set(startNode.id, 0);
		paths.set(startNode.id, 1);
		queue.push(startNode.id);

		// BFS to find shortest paths
		while (queue.length > 0) {
			const currentId = queue.shift();
			if (!currentId) break;
			const currentDist = distances.get(currentId);
			if (currentDist === undefined) continue;
			const neighbors = adjacency.get(currentId) || new Set();

			for (const neighborId of neighbors) {
				const neighborDist = distances.get(neighborId);

				if (neighborDist === undefined || neighborDist === -1) {
					// First time reaching this node
					distances.set(neighborId, currentDist + 1);
					queue.push(neighborId);
				}

				if (distances.get(neighborId) === currentDist + 1) {
					// Found a shortest path
					const currentPaths = paths.get(currentId) || 0;
					paths.set(neighborId, (paths.get(neighborId) || 0) + currentPaths);
					const predList = predecessors.get(neighborId);
					if (predList) {
						predList.push(currentId);
					}
				}
			}
		}

		// Calculate dependencies (backward pass)
		const dependencies = new Map<string, number>();
		nodes.forEach((node) => {
			dependencies.set(node.id, 0);
		});

		// Process nodes in reverse order of distance
		const sortedNodes = Array.from(nodes).sort((a, b) => (distances.get(b.id) || 0) - (distances.get(a.id) || 0));

		sortedNodes.forEach((node) => {
			if (node.id === startNode.id) return;

			const nodeDeps = dependencies.get(node.id) ?? 0;
			const nodePaths = paths.get(node.id) ?? 0;

			const predList = predecessors.get(node.id);
			if (predList) {
				predList.forEach((predId) => {
					const predPaths = paths.get(predId) ?? 0;
					const contribution = (predPaths / nodePaths) * (1 + nodeDeps);
					dependencies.set(predId, (dependencies.get(predId) || 0) + contribution);
				});
			}

			if (node.id !== startNode.id) {
				betweenness.set(node.id, (betweenness.get(node.id) || 0) + nodeDeps);
			}
		});
	});

	// Normalize for undirected graphs (divide by 2)
	nodes.forEach((node) => {
		betweenness.set(node.id, (betweenness.get(node.id) || 0) / 2);
	});

	return betweenness;
}

/**
 * Calculate eigenvector centrality using power iteration
 * Simplified version with max iterations
 */
export function calculateEigenvectorCentrality(
	nodes: VisualNode[],
	edges: VisualEdge[],
	maxIterations: number = 100,
): Map<string, number> {
	const adjacency = buildAdjacencyList(nodes, edges);
	const centrality = new Map<string, number>();

	// Initialize with equal values
	const n = nodes.length;
	nodes.forEach((node) => {
		centrality.set(node.id, 1.0 / n);
	});

	// Power iteration
	for (let iter = 0; iter < maxIterations; iter++) {
		const newCentrality = new Map<string, number>();
		let sumSquares = 0;

		nodes.forEach((node) => {
			let sum = 0;
			const neighbors = adjacency.get(node.id) || new Set();

			neighbors.forEach((neighborId) => {
				sum += centrality.get(neighborId) || 0;
			});

			newCentrality.set(node.id, sum);
			sumSquares += sum * sum;
		});

		// Normalize
		const norm = Math.sqrt(sumSquares);
		if (norm < 1e-10) break; // Convergence check

		nodes.forEach((node) => {
			centrality.set(node.id, (newCentrality.get(node.id) || 0) / norm);
		});

		// Check for convergence (simplified)
		let maxDiff = 0;
		nodes.forEach((node) => {
			const diff = Math.abs((newCentrality.get(node.id) || 0) / norm - (centrality.get(node.id) || 0));
			maxDiff = Math.max(maxDiff, diff);
		});

		if (maxDiff < 1e-6) break;
	}

	return centrality;
}

/**
 * Calculate node sizes based on centrality metrics
 * Uses weighted combination: 50% degree, 30% betweenness, 20% eigenvector
 */
export function calculateNodeSizes(
	nodes: VisualNode[],
	degree: Map<string, number>,
	betweenness: Map<string, number>,
	eigenvector: Map<string, number>,
): Map<string, number> {
	// Find max values for normalization
	const maxDegree = Math.max(...Array.from(degree.values()), 1);
	const maxBetweenness = Math.max(...Array.from(betweenness.values()), 1);
	const maxEigenvector = Math.max(...Array.from(eigenvector.values()), 1);

	const sizes = new Map<string, number>();

	nodes.forEach((node) => {
		const degreeNorm = (degree.get(node.id) || 0) / maxDegree;
		const betweennessNorm = maxBetweenness > 0 ? (betweenness.get(node.id) || 0) / maxBetweenness : 0;
		const eigenvectorNorm = maxEigenvector > 0 ? (eigenvector.get(node.id) || 0) / maxEigenvector : 0;

		// Weighted importance score
		const importance = 0.5 * degreeNorm + 0.3 * betweennessNorm + 0.2 * eigenvectorNorm;

		// Scale to 10-30 range
		const size = 10 + 20 * importance;
		sizes.set(node.id, size);
	});

	return sizes;
}

/**
 * Colorblind-friendly community color palette
 */
export const COMMUNITY_COLORS = [
	"#e41a1c", // Red
	"#377eb8", // Blue
	"#4daf4a", // Green
	"#984ea3", // Purple
	"#ff7f00", // Orange
	"#ffff33", // Yellow
	"#a65628", // Brown
	"#f781bf", // Pink
];

/**
 * Get color for a community ID
 */
export function getCommunityColor(communityId: number): string {
	return COMMUNITY_COLORS[communityId % COMMUNITY_COLORS.length];
}
